/**
 * Thin fetch wrapper. Returns parsed JSON or throws with a typed error.
 * All API responses follow the { ok, data } | { ok, error } envelope from
 * @qb/shared.
 *
 * Auth: attaches `Authorization: Bearer <token>` from localStorage if present.
 * On 401, fires a global hook so the auth store can clear state and the
 * router can redirect to /login. The hook is registered in the auth store.
 */

import type { ApiResponse } from '@qb/shared';

export class ApiClientError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

const BASE = '/api';
const TOKEN_KEY = 'qb.token';

export function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(TOKEN_KEY);
}

/** Global hook fired on 401 — auth store wires it to `logout + redirect`. */
let onUnauthorized: (() => void) | null = null;
export function setOnUnauthorized(handler: () => void): void {
  onUnauthorized = handler;
}

export async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  headers.set('Accept', 'application/json');

  const token = getToken();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  // Hard timeout: without it a slow/stuck request hangs forever, holds an HTTP
  // connection, and after ~6 of them the browser stops issuing ANY request to
  // the host until a reload (this jammed the Assign-to-project modal). Abort
  // after 20s so the connection is freed and the caller can recover/retry.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 20_000);
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      headers,
      signal: init.signal ?? controller.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new ApiClientError('TIMEOUT', `Request to ${path} timed out`, 0);
    }
    throw new ApiClientError('NETWORK', `Network error on ${path}`, 0);
  } finally {
    clearTimeout(timer);
  }

  if (res.status === 401) {
    clearToken();
    onUnauthorized?.();
  }

  let payload: ApiResponse<T> | undefined;
  try {
    payload = (await res.json()) as ApiResponse<T>;
  } catch {
    throw new ApiClientError('INVALID_JSON', `Bad JSON from ${path}`, res.status);
  }

  if (!payload.ok) {
    throw new ApiClientError(
      payload.error.code,
      payload.error.message,
      res.status,
      payload.error.details,
    );
  }
  return payload.data;
}

export const http = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
